import {
  Eye,
  Scan,
  CircleDot,
  Scissors,
  Brain,
  Move,
  Circle,
  Crosshair,
  type LucideIcon,
} from "lucide-react";

export interface SubspecialtyConfig {
  id: string;
  name: string;
  nameEn: string;
  icon: LucideIcon;
  description: string;
  color: string;
  contentDir: string;
}

export const SUBSPECIALTIES: SubspecialtyConfig[] = [
  {
    id: "cornea",
    name: "각막/외안부",
    nameEn: "Cornea & External Eye",
    icon: Eye,
    description: "감염성 각막질환, 각막이영양증, 결막, 공막, 안구표면질환",
    color: "clinical-blue",
    contentDir: "cornea",
  },
  {
    id: "refraction",
    name: "굴절/광학",
    nameEn: "Refraction & Optics",
    icon: Crosshair,
    description: "굴절이상, 노안, 굴절교정수술",
    color: "clinical-purple",
    contentDir: "refraction",
  },
  {
    id: "glaucoma",
    name: "녹내장",
    nameEn: "Glaucoma",
    icon: CircleDot,
    description: "개방각, 폐쇄각, 이차성, 선천 녹내장, 수술",
    color: "clinical-green",
    contentDir: "glaucoma",
  },
  {
    id: "retina",
    name: "망막",
    nameEn: "Retina",
    icon: Scan,
    description: "혈관성 망막질환, 황반질환, 유리체망막, 포도막염",
    color: "clinical-red",
    contentDir: "retina",
  },
  {
    id: "cataract",
    name: "백내장",
    nameEn: "Cataract",
    icon: Circle,
    description: "분류, 수술, IOL, 합병증",
    color: "clinical-green",
    contentDir: "cataract",
  },
  {
    id: "oculoplastics",
    name: "성형안과",
    nameEn: "Oculoplastics",
    icon: Scissors,
    description: "눈꺼풀, 눈물기관, 안와질환",
    color: "clinical-purple",
    contentDir: "oculoplastics",
  },
  {
    id: "strabismus",
    name: "사시/소아안과",
    nameEn: "Strabismus & Pediatric",
    icon: Move,
    description: "내사시, 외사시, 약시, 선천백내장",
    color: "clinical-blue",
    contentDir: "strabismus",
  },
  {
    id: "neuro-ophthalmology",
    name: "신경안과",
    nameEn: "Neuro-ophthalmology",
    icon: Brain,
    description: "시신경, 동공, 뇌신경마비, 안진",
    color: "clinical-amber",
    contentDir: "neuro-ophthalmology",
  },
];

export const SUBSPECIALTY_MAP = Object.fromEntries(
  SUBSPECIALTIES.map((s) => [s.id, s])
);

export const SITE_CONFIG = {
  name: "OphJour",
  nameKo: "안과 지식 베이스",
  description: "안과 전문의/전공의를 위한 프리미엄 의학 지식 플랫폼",
  url: "https://ophjour.com",
};

export type ViewMode = "standard" | "quick";

export type EmergencyLevel = "emergency" | "semi-emergency" | "non-emergency";

export type ExamImportance = "high-yield" | "moderate" | "low-yield";

export type ArticleType =
  | "meta-analysis"
  | "rct"
  | "guideline"
  | "review"
  | "original"
  | "case-series"
  | "case-report";

export const ARTICLE_TYPE_SCORES: Record<ArticleType, number> = {
  "meta-analysis": 100,
  guideline: 95,
  rct: 90,
  review: 70,
  original: 60,
  "case-series": 40,
  "case-report": 20,
};

export const ARTICLE_TYPE_LABELS: Record<ArticleType, string> = {
  "meta-analysis": "Meta-analysis",
  guideline: "Guideline",
  rct: "RCT",
  review: "Review",
  original: "Original",
  "case-series": "Case Series",
  "case-report": "Case Report",
};

export const IMAGE_TYPES = [
  { id: "fundus", name: "안저 사진", nameEn: "Fundus Photo" },
  { id: "slit-lamp", name: "세극등", nameEn: "Slit Lamp" },
  { id: "oct", name: "OCT", nameEn: "OCT" },
  { id: "fag", name: "FAG/ICGA", nameEn: "FAG/ICGA" },
  { id: "visual-field", name: "시야검사", nameEn: "Visual Field" },
  { id: "external", name: "외안부", nameEn: "External Photo" },
  { id: "gonioscopy", name: "전방각경", nameEn: "Gonioscopy" },
] as const;
